import { cn } from '@/lib/utils';

/** 'all' | 'mine' | 'unassigned' | a principalId */
export type AssigneeFilterValue = string;

interface AssigneeFilterBarProps {
  value: AssigneeFilterValue;
  onChange: (value: AssigneeFilterValue) => void;
  /** Active members of the workspace, for per-member filtering. */
  members: Array<{ principalId: string; label: string }>;
  /** The caller's principalId — drives "My tasks" and is excluded from member chips. */
  myPrincipalId?: string | null;
  /** Whether to show per-member chips. Managers see who has what; members only filter to themselves. */
  showMembers: boolean;
}

/**
 * Filter the list by assignee. "Everyone" shows the full sectioned list;
 * picking a person (yourself, a teammate, or Unassigned) shows just their
 * tasks. Managers get a chip per member to review each person's workload;
 * everyone gets "My tasks".
 */
export function AssigneeFilterBar({
  value,
  onChange,
  members,
  myPrincipalId,
  showMembers,
}: AssigneeFilterBarProps) {
  const chip = (key: string, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(key)}
      aria-pressed={value === key}
      className={cn(
        'px-2.5 py-1 rounded-full text-xs border transition-colors',
        value === key
          ? 'bg-primary text-primary-foreground border-primary'
          : 'border-border text-muted-foreground hover:bg-accent',
      )}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-0.5 text-xs text-muted-foreground">Show:</span>
      {chip('all', 'Everyone')}
      {myPrincipalId && chip('mine', 'My tasks')}
      {chip('unassigned', 'Unassigned')}
      {showMembers &&
        members
          .filter((m) => m.principalId !== myPrincipalId)
          .map((m) => chip(m.principalId, m.label))}
    </div>
  );
}
